import { useState, useMemo } from "react";
import {
  User,
  Shield,
  Edit2,
  Trash2,
  Plus,
  Search,
  Check,
  X,
  Lock,
  Mail,
  Globe,
  FileText,
  UserCheck,
  UserX,
  Power,
  RefreshCw,
  Award
} from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { apiSend } from "../../services/api";
import { useAuth } from "../../services/auth";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { EmptyState } from "../../components/common/EmptyState";
import { formatDate } from "../../utils/date";

export interface UserAccount {
  id: number;
  name: string;
  username: string;
  email: string;
  status: "active" | "inactive" | "suspended";
  failed_login_count: number;
  last_login_at: string | null;
  created_at: string;
  role_ids: number[];
  roles: string[];
  author?: {
    id: number;
    slug: string;
    bio: string | null;
    website: string | null;
  } | null;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions?: string;
}

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const { data: users, loading, error } = useApi<UserAccount[]>("/api/admin/users", [reloadTrigger]);
  const { data: roles } = useApi<Role[]>("/api/admin/roles", []);

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserAccount | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    selectedRoles: ["Author"] as string[],
    status: "active" as "active" | "inactive",
    isAuthor: true,
    authorSlug: "",
    authorBio: "",
    authorWebsite: ""
  });

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    return users.filter((u) => {
      const matchSearch =
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.author?.slug && u.author.slug.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchRole =
        roleFilter === "all" ||
        u.roles.some((r) => r.toLowerCase() === roleFilter.toLowerCase());

      return matchSearch && matchRole;
    });
  }, [users, searchQuery, roleFilter]);

  const kpis = useMemo(() => {
    if (!users) return { total: 0, superAdmins: 0, authors: 0, editors: 0, moderators: 0 };
    return {
      total: users.length,
      superAdmins: users.filter((u) => u.roles.includes("Super Administrator") || u.roles.includes("Administrator")).length,
      authors: users.filter((u) => u.roles.includes("Author") || u.author !== null).length,
      editors: users.filter((u) => u.roles.includes("Editor")).length,
      moderators: users.filter((u) => u.roles.includes("Moderator")).length
    };
  }, [users]);

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      username: "",
      email: "",
      password: "",
      selectedRoles: ["Author"],
      status: "active",
      isAuthor: true,
      authorSlug: "",
      authorBio: "Staff contributor and investigative reporter at ZimSmart Magazine.",
      authorWebsite: "https://zimsmart.co.zw"
    });
    setFormError("");
    setFormSuccess("");
    setIsModalOpen(true);
  };

  const handleOpenEdit = (user: UserAccount) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      username: user.username,
      email: user.email,
      password: "",
      selectedRoles: user.roles.length ? user.roles : ["Author"],
      status: user.status === "active" ? "active" : "inactive",
      isAuthor: Boolean(user.author || user.roles.includes("Author")),
      authorSlug: user.author?.slug || "",
      authorBio: user.author?.bio || "",
      authorWebsite: user.author?.website || ""
    });
    setFormError("");
    setFormSuccess("");
    setIsModalOpen(true);
  };

  const handleToggleStatus = async (user: UserAccount) => {
    if (user.id === currentUser?.id) {
      alert("You cannot deactivate your own logged-in administrator account.");
      return;
    }

    const nextStatus = user.status === "active" ? "inactive" : "active";
    try {
      await apiSend(`/api/admin/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus })
      });
      setReloadTrigger((n) => n + 1);
    } catch (err: any) {
      alert(err.message || "Failed to update user status");
    }
  };

  const handleDeleteUser = async (user: UserAccount) => {
    if (user.id === currentUser?.id) {
      alert("You cannot delete your own logged-in administrator account.");
      return;
    }

    if (!window.confirm(`Are you sure you want to deactivate and remove staff account "${user.name}"?`)) {
      return;
    }

    try {
      await apiSend(`/api/admin/users/${user.id}`, {
        method: "DELETE"
      });
      setReloadTrigger((n) => n + 1);
    } catch (err: any) {
      alert(err.message || "Failed to delete user");
    }
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.username.trim() || !formData.email.trim()) {
      setFormError("Full name, username, and email address are required");
      return;
    }

    if (!editingUser && !formData.password.trim()) {
      setFormError("A secure initial password is required for new accounts");
      return;
    }

    if (formData.selectedRoles.length === 0) {
      setFormError("Please select at least one role for this account");
      return;
    }

    setSaving(true);
    setFormError("");

    try {
      const payload: any = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        email: formData.email.trim(),
        roles: formData.selectedRoles,
        status: formData.status
      };

      if (formData.password.trim()) {
        payload.password = formData.password.trim();
      }

      if (formData.isAuthor || formData.selectedRoles.includes("Author")) {
        payload.author_bio = formData.authorBio.trim();
        payload.author_website = formData.authorWebsite.trim();
        if (formData.authorSlug.trim()) {
          payload.author_slug = formData.authorSlug.trim();
        }
      }

      if (editingUser) {
        await apiSend(`/api/admin/users/${editingUser.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload)
        });
        setFormSuccess("Staff account and author profile updated successfully!");
      } else {
        await apiSend("/api/admin/users", {
          method: "POST",
          body: JSON.stringify(payload)
        });
        setFormSuccess("New staff account registered and author profile configured!");
      }

      setTimeout(() => {
        setIsModalOpen(false);
        setFormSuccess("");
        setReloadTrigger((n) => n + 1);
      }, 700);
    } catch (err: any) {
      setFormError(err.message || "Failed to save user account");
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case "Super Administrator":
        return "bg-purple-100 text-purple-900 border-purple-300";
      case "Administrator":
        return "bg-blue-100 text-blue-900 border-blue-300";
      case "Editor":
        return "bg-amber-100 text-amber-900 border-amber-300";
      case "Author":
        return "bg-emerald-100 text-emerald-900 border-emerald-300";
      case "Moderator":
        return "bg-sky-100 text-sky-900 border-sky-300";
      default:
        return "bg-slate-100 text-slate-800 border-slate-300";
    }
  };

  return (
    <section className="admin-page">
      <header className="admin-page-header flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1>Staff, Authors & Moderator Management</h1>
            <span className="text-xs font-bold uppercase tracking-wider bg-emerald-100 text-emerald-900 px-2.5 py-0.5 rounded-full border border-emerald-300">
              Access Control Active
            </span>
          </div>
          <p className="text-slate-600 mt-1">
            Super Administrator console to register journalists, authors, editorial managers, and moderation staff.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleOpenCreate}
            className="mag-admin-primary-btn"
          >
            <Plus size={16} />
            <span>Add Author / Moderator / Staff</span>
          </button>
        </div>
      </header>

      {/* KPI Stats Grid */}
      <div className="mag-admin-metric-grid">
        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Total Staff</span>
            <User size={16} className="text-slate-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{kpis.total}</div>
        </div>

        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Administrators</span>
            <Shield size={16} className="text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{kpis.superAdmins}</div>
        </div>

        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Authors & Journalists</span>
            <Award size={16} className="text-emerald-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{kpis.authors}</div>
        </div>

        <div className="mag-admin-stat-card">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs uppercase font-bold tracking-wider">Editors & Moderators</span>
            <UserCheck size={16} className="text-amber-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{kpis.editors + kpis.moderators}</div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="mag-admin-filters-bar flex flex-wrap items-center justify-between gap-4 bg-white p-4 border border-slate-200 rounded-lg mb-6">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Role Filter:</span>
          <div className="flex flex-wrap items-center gap-1.5 bg-slate-100 p-1 rounded-md">
            {[
              { id: "all", label: "All Roles" },
              { id: "Super Administrator", label: "Super Admin" },
              { id: "Administrator", label: "Admin" },
              { id: "Editor", label: "Editor" },
              { id: "Author", label: "Author" },
              { id: "Moderator", label: "Moderator" }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setRoleFilter(tab.id)}
                className={`px-3 py-1 text-xs font-semibold rounded transition-all ${
                  roleFilter === tab.id
                    ? "bg-white text-slate-900 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, author slug..."
            className="w-full text-xs pl-9 pr-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:border-slate-500"
          />
        </div>
      </div>

      {/* Users & Authors Listing Table */}
      {loading ? (
        <LoadingState label="Loading staff accounts and author directory..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : !filteredUsers.length ? (
        <div className="admin-card-panel p-0 overflow-hidden">
          <EmptyState
            icon={User}
            badge="STAFF DIRECTORY"
            title="No staff members found"
            description="No team members match your current filters. Register a new author, editor, or administrator with customized permissions."
            actionText="Add Staff Member"
            onAction={handleOpenCreate}
            actionIcon={Plus}
          />
        </div>
      ) : (
        <div className="admin-card-panel p-0 overflow-hidden">
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Member Name & Account</th>
                  <th>Assigned Role(s)</th>
                  <th>Author Profile & Slug</th>
                  <th>Created Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((member) => {
                  const isCurrent = member.id === currentUser?.id;
                  return (
                    <tr key={member.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-900 text-white font-serif font-bold text-xs flex items-center justify-center shrink-0">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 flex items-center gap-2 text-xs">
                              <span>{member.name}</span>
                              {isCurrent && (
                                <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.2 rounded font-mono font-bold">
                                  YOU
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                              <span>@{member.username}</span>
                              <span>•</span>
                              <span>{member.email}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="flex flex-wrap gap-1.5">
                          {member.roles.map((role) => (
                            <span
                              key={role}
                              className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getRoleBadgeClass(
                                role
                              )}`}
                            >
                              {role}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td>
                        {member.author ? (
                          <div>
                            <div className="font-mono text-xs text-emerald-800 font-semibold flex items-center gap-1">
                              <span>/author/{member.author.slug}</span>
                            </div>
                            {member.author.bio && (
                              <p className="text-xs text-slate-500 line-clamp-1 max-w-xs mt-0.5">
                                {member.author.bio}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No public author profile</span>
                        )}
                      </td>

                      <td>
                        <span className="text-xs font-mono text-slate-600">
                          {formatDate(member.created_at)}
                        </span>
                      </td>

                      <td>
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(member)}
                          disabled={isCurrent}
                          className={`status-pill ${
                            member.status === "active" ? "status-published" : "status-archived"
                          } ${isCurrent ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                          title={isCurrent ? "Cannot deactivate yourself" : member.status === "active" ? "Click to Deactivate" : "Click to Activate"}
                        >
                          <span className="status-dot" />
                          <span>{member.status === "active" ? "Active" : "Inactive"}</span>
                        </button>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <div className="admin-row-actions">
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(member)}
                            className="action-icon-btn"
                            title="Edit Staff Member & Author Profile"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteUser(member)}
                            disabled={isCurrent}
                            className={`action-icon-btn delete ${
                              isCurrent ? "opacity-30 cursor-not-allowed" : ""
                            }`}
                            title={isCurrent ? "Cannot delete yourself" : "Deactivate / Delete Account"}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CREATE / EDIT STAFF MEMBER & AUTHOR MODAL */}
      {isModalOpen && (
        <div className="mag-modal-backdrop" onClick={() => setIsModalOpen(false)}>
          <div
            className="mag-modal-card max-w-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mag-modal-close-btn"
            >
              <X size={18} />
            </button>

            <div className="p-6">
              <div className="pb-3 mb-4 border-b border-slate-200">
                <h2 className="text-xl font-serif font-bold text-slate-900">
                  {editingUser ? `Edit Staff Member: ${editingUser.name}` : "Add Staff Member, Author or Moderator"}
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Configure administrative credentials, system roles, and public editorial author profile.
                </p>
              </div>

              {formError && <div className="mag-modal-error mb-4">{formError}</div>}
              {formSuccess && (
                <div className="p-3 bg-emerald-50 text-emerald-800 text-sm font-semibold rounded mb-4 flex items-center gap-2">
                  <Check size={16} /> {formSuccess}
                </div>
              )}

              <form onSubmit={handleSaveUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Tendai Chikore"
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-600"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="e.g. tchikore"
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-slate-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="e.g. tendai@zimsmart.co.zw"
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-600"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      {editingUser ? "New Password (Leave blank to keep)" : "Password *"}
                    </label>
                    <input
                      type="password"
                      required={!editingUser}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={editingUser ? "••••••••" : "Create strong password"}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-slate-600"
                    />
                  </div>
                </div>

                {/* Role Assignment */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                    Assign System Role(s) *
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    {[
                      { name: "Super Administrator", desc: "Full master access across all modules" },
                      { name: "Administrator", desc: "Platform and operations manager" },
                      { name: "Editor", desc: "Can edit, curate and publish articles" },
                      { name: "Author", desc: "Can create articles & has author byline" },
                      { name: "Moderator", desc: "Reviews comments & community reports" }
                    ].map((roleOption) => {
                      const isChecked = formData.selectedRoles.includes(roleOption.name);
                      return (
                        <label
                          key={roleOption.name}
                          className={`flex items-start gap-2.5 p-2 rounded border cursor-pointer transition-all ${
                            isChecked
                              ? "bg-amber-50 border-amber-300 text-slate-900"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  selectedRoles: [...formData.selectedRoles, roleOption.name]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  selectedRoles: formData.selectedRoles.filter((r) => r !== roleOption.name)
                                });
                              }
                            }}
                            className="mt-0.5 rounded text-amber-600 focus:ring-amber-500"
                          />
                          <div>
                            <div className="text-xs font-bold">{roleOption.name}</div>
                            <div className="text-[10px] text-slate-500 leading-tight mt-0.5">
                              {roleOption.desc}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Public Author Profile Section */}
                <div className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-emerald-950 flex items-center gap-1.5">
                      <Award size={14} className="text-emerald-700" />
                      <span>Editorial Author Profile & Byline</span>
                    </span>
                    <span className="text-[11px] text-emerald-800 font-medium">
                      Appears on article bylines and public author pages
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Author URL Slug (e.g. tendai-chikore)
                      </label>
                      <input
                        type="text"
                        value={formData.authorSlug}
                        onChange={(e) => setFormData({ ...formData, authorSlug: e.target.value })}
                        placeholder="Leave blank to auto-generate from name"
                        className="w-full border border-slate-300 rounded px-3 py-1.5 text-xs font-mono bg-white focus:outline-none focus:border-emerald-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Personal / Professional Website
                      </label>
                      <input
                        type="url"
                        value={formData.authorWebsite}
                        onChange={(e) => setFormData({ ...formData, authorWebsite: e.target.value })}
                        placeholder="https://example.com"
                        className="w-full border border-slate-300 rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:border-emerald-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Author Biography & Editorial Focus
                    </label>
                    <textarea
                      rows={2}
                      value={formData.authorBio}
                      onChange={(e) => setFormData({ ...formData, authorBio: e.target.value })}
                      placeholder="Write a brief professional bio highlighting coverage beats, research expertise, or journalistic history..."
                      className="w-full border border-slate-300 rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-slate-700 uppercase">Account Status:</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="text-xs border border-slate-300 rounded px-2.5 py-1 bg-white font-semibold"
                    >
                      <option value="active">Active (Can Sign In)</option>
                      <option value="inactive">Inactive / Suspended</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 bg-slate-100 rounded hover:bg-slate-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="mag-admin-primary-btn"
                    >
                      {saving ? "Saving..." : editingUser ? "Save Changes" : "Create Staff Account"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
